import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import Text from "../../../components/atoms/Text";
import Button from "../../../components/atoms/Button";
import Spinner from "../../../components/atoms/Spinner";
import { formatGender } from "../../../utils/gender";
import {
  fetchAdminConsultationRecordById,
  getConsultationStatusLabel,
  getConsultationStatusColor,
} from "../../../api/consultationService";
import { ConsultationRecordAdmin } from "common/models/types";

// 저장형 XSS 방어(spec 설계 결정 12 — 보안 한정 예외). MarkdownViewer와 같은 목록.
const DISALLOWED_ELEMENTS = ["script", "iframe", "style", "form", "object", "embed", "link", "base"];

const PopupContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background: #f5f5f5;
  padding: 20px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #fff;
  border-radius: 8px;
  margin-bottom: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const Title = styled(Text)`
  font-size: 20px;
  font-weight: 600;
  color: #333;
`;

const CloseButton = styled(Button)`
  min-width: 80px;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Section = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const SectionTitle = styled(Text)`
  font-size: 16px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e5e7eb;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const InfoLabel = styled(Text)`
  font-size: 12px;
  color: #6b7280;
`;

const InfoValue = styled(Text)`
  font-size: 14px;
  color: #374151;
  font-weight: 500;
`;

const StatusBadge = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  color: #fff;
  background-color: ${(props) => props.$color};
`;

const AudioSection = styled.div`
  margin-top: 8px;

  audio {
    width: 100%;
    height: 40px;
  }
`;

const RawTextSection = styled.div`
  max-height: 300px;
  overflow-y: auto;
  background: #f9fafb;
  border-radius: 4px;
  padding: 16px;
  font-size: 14px;
  line-height: 1.6;
  white-space: pre-wrap;
  color: #374151;
`;

const MarkdownSection = styled.div`
  background: #fff;
  padding: 16px;
  border-radius: 4px;
  border: 1px solid #e5e7eb;

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin-top: 16px;
    margin-bottom: 8px;
    font-weight: 600;
    color: #1f2937;
  }

  h1 {
    font-size: 24px;
  }
  h2 {
    font-size: 20px;
  }
  h3 {
    font-size: 18px;
  }

  p {
    margin-bottom: 12px;
    line-height: 1.6;
  }

  ul,
  ol {
    margin-left: 20px;
    margin-bottom: 12px;
  }

  li {
    margin-bottom: 4px;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 16px;
  }

  th,
  td {
    border: 1px solid #e5e7eb;
    padding: 8px 12px;
    text-align: left;
  }

  th {
    background: #f9fafb;
    font-weight: 600;
  }

  code {
    background: #f3f4f6;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 13px;
  }

  pre {
    background: #1f2937;
    color: #f9fafb;
    padding: 16px;
    border-radius: 8px;
    overflow-x: auto;
    margin-bottom: 16px;
  }

  blockquote {
    border-left: 4px solid #4a90e2;
    padding-left: 16px;
    margin: 16px 0;
    color: #6b7280;
  }
`;

const AnalysisGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 16px;
`;

const AnalysisItem = styled.div`
  background: #f9fafb;
  padding: 12px;
  border-radius: 6px;
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 16px;
  margin-top: 8px;

  p {
    color: #dc2626;
    font-size: 14px;
    margin: 0;
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`;

const AdminConsultationRecordPopup: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [record, setRecord] = useState<ConsultationRecordAdmin | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadRecord = async () => {
      if (!id) {
        setError("잘못된 접근입니다.");
        setLoading(false);
        return;
      }

      try {
        const response = await fetchAdminConsultationRecordById(Number(id));

        if (response.error === 0 && response.data) {
          setRecord(response.data);
        } else {
          setError(response.message || "데이터를 불러오는데 실패했습니다.");
        }
      } catch (err) {
        console.error("Failed to load consultation record:", err);
        setError("데이터를 불러오는데 실패했습니다.");
      } finally {
        setLoading(false);
      }
    };

    loadRecord();
  }, [id]);

  const handleClose = () => {
    window.close();
  };

  // 날짜 포맷팅
  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleString("ko-KR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  // 처리 시간 포맷팅
  const formatProcessingTime = (ms?: number): string => {
    if (!ms) return "-";
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}초`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}분 ${remainingSeconds}초`;
  };

  // 오디오 길이 포맷팅
  const formatAudioDuration = (seconds?: number): string => {
    if (!seconds) return "-";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };


  if (loading) {
    return (
      <PopupContainer>
        <LoadingWrapper>
          <Spinner />
        </LoadingWrapper>
      </PopupContainer>
    );
  }

  if (error || !record) {
    return (
      <PopupContainer>
        <Header>
          <Title>상담 기록 상세</Title>
          <CloseButton
            size="Small"
            type="Text"
            variant="Outlined"
            color="Secondary"
            onClick={handleClose}
          >
            닫기
          </CloseButton>
        </Header>
        <Section>
          <Text color="#dc2626">{error || "데이터를 찾을 수 없습니다."}</Text>
        </Section>
      </PopupContainer>
    );
  }

  return (
    <PopupContainer>
      <Header>
        <Title>상담 기록 상세 - #{record.id}</Title>
        <CloseButton
          size="Small"
          type="Text"
          variant="Outlined"
          color="Secondary"
          onClick={handleClose}
        >
          닫기
        </CloseButton>
      </Header>

      <ContentWrapper>
        {/* 기본 정보 */}
        <Section>
          <SectionTitle>기본 정보</SectionTitle>
          <InfoGrid>
            <InfoItem>
              <InfoLabel>ID</InfoLabel>
              <InfoValue>{record.id}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>사용자</InfoLabel>
              <InfoValue>
                {record.userName || "-"} ({record.userEmail || "-"})
              </InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>상태</InfoLabel>
              <StatusBadge $color={getConsultationStatusColor(record.status)}>
                {getConsultationStatusLabel(record.status)}
              </StatusBadge>
            </InfoItem>
            <InfoItem>
              <InfoLabel>생성일</InfoLabel>
              <InfoValue>{formatDate(record.createdAt)}</InfoValue>
            </InfoItem>
          </InfoGrid>

          {record.errorMessage && (
            <ErrorMessage>
              <InfoLabel style={{ marginBottom: 4 }}>오류 메시지</InfoLabel>
              <p>{record.errorMessage}</p>
            </ErrorMessage>
          )}
        </Section>

        {/* 입력 컨텍스트 */}
        {record.context && (
          <Section>
            <SectionTitle>입력 컨텍스트</SectionTitle>
            <InfoGrid>
              {record.context.patientName && (
                <InfoItem>
                  <InfoLabel>환자명</InfoLabel>
                  <InfoValue>{record.context.patientName}</InfoValue>
                </InfoItem>
              )}
              {record.context.patientAge && (
                <InfoItem>
                  <InfoLabel>나이</InfoLabel>
                  <InfoValue>{record.context.patientAge}세</InfoValue>
                </InfoItem>
              )}
              {record.context.patientGender && (
                <InfoItem>
                  <InfoLabel>성별</InfoLabel>
                  <InfoValue>{formatGender(record.context.patientGender)}</InfoValue>
                </InfoItem>
              )}
              {record.context.conditions && (
                <InfoItem>
                  <InfoLabel>기저질환</InfoLabel>
                  <InfoValue>{record.context.conditions}</InfoValue>
                </InfoItem>
              )}
              {record.context.medications && (
                <InfoItem>
                  <InfoLabel>복용약물</InfoLabel>
                  <InfoValue>{record.context.medications}</InfoValue>
                </InfoItem>
              )}
              {record.context.additionalInfo && (
                <InfoItem style={{ gridColumn: "1 / -1" }}>
                  <InfoLabel>추가정보</InfoLabel>
                  <InfoValue>{record.context.additionalInfo}</InfoValue>
                </InfoItem>
              )}
            </InfoGrid>
          </Section>
        )}

        {/* 오디오 */}
        {record.audioUrl && (
          <Section>
            <SectionTitle>
              오디오
              {record.audioDurationSeconds && (
                <span style={{ fontWeight: 400, color: "#6b7280", marginLeft: 8 }}>
                  ({formatAudioDuration(record.audioDurationSeconds)})
                </span>
              )}
            </SectionTitle>
            <AudioSection>
              <audio controls src={record.audioUrl}>
                브라우저가 오디오를 지원하지 않습니다.
              </audio>
            </AudioSection>
          </Section>
        )}

        {/* STT 원본 텍스트 */}
        {record.rawText && (
          <Section>
            <SectionTitle>STT 원본 텍스트</SectionTitle>
            <RawTextSection>{record.rawText}</RawTextSection>
          </Section>
        )}

        {/* 기록지 내용 */}
        {record.recordContent && (
          <Section>
            <SectionTitle>기록지 내용</SectionTitle>
            <MarkdownSection>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
                disallowedElements={DISALLOWED_ELEMENTS}
              >
                {record.recordContent}
              </ReactMarkdown>
            </MarkdownSection>
          </Section>
        )}

        {/* 처리 분석 */}
        {record.analysis && (
          <Section>
            <SectionTitle>처리 분석</SectionTitle>
            <AnalysisGrid>
              <AnalysisItem>
                <InfoLabel>파이프라인</InfoLabel>
                <InfoValue>{record.analysis.pipeline || "-"}</InfoValue>
              </AnalysisItem>
              <AnalysisItem>
                <InfoLabel>STT 모델</InfoLabel>
                <InfoValue>{record.analysis.sttModel || "-"}</InfoValue>
              </AnalysisItem>
              <AnalysisItem>
                <InfoLabel>LLM 모델</InfoLabel>
                <InfoValue>{record.analysis.llmModel || "-"}</InfoValue>
              </AnalysisItem>
              <AnalysisItem>
                <InfoLabel>처리 시간</InfoLabel>
                <InfoValue>{formatProcessingTime(record.analysis.processingTimeMs)}</InfoValue>
              </AnalysisItem>
              <AnalysisItem>
                <InfoLabel>시작 시간</InfoLabel>
                <InfoValue>{formatDate(record.analysis.startedAt)}</InfoValue>
              </AnalysisItem>
              <AnalysisItem>
                <InfoLabel>완료 시간</InfoLabel>
                <InfoValue>{formatDate(record.analysis.completedAt)}</InfoValue>
              </AnalysisItem>
              {record.analysis.cost && (
                <>
                  <AnalysisItem>
                    <InfoLabel>STT 비용</InfoLabel>
                    <InfoValue>${record.analysis.cost.stt?.toFixed(4) || "0"}</InfoValue>
                  </AnalysisItem>
                  <AnalysisItem>
                    <InfoLabel>LLM 비용</InfoLabel>
                    <InfoValue>${record.analysis.cost.llm?.toFixed(4) || "0"}</InfoValue>
                  </AnalysisItem>
                  <AnalysisItem>
                    <InfoLabel>총 비용</InfoLabel>
                    <InfoValue style={{ color: "#4a90e2", fontWeight: 600 }}>
                      ${record.analysis.cost.total?.toFixed(4) || "0"}
                    </InfoValue>
                  </AnalysisItem>
                </>
              )}
              {record.analysis.tokens && (
                <>
                  <AnalysisItem>
                    <InfoLabel>프롬프트 토큰</InfoLabel>
                    <InfoValue>
                      {record.analysis.tokens.prompt?.toLocaleString() || "-"}
                    </InfoValue>
                  </AnalysisItem>
                  <AnalysisItem>
                    <InfoLabel>완료 토큰</InfoLabel>
                    <InfoValue>
                      {record.analysis.tokens.completion?.toLocaleString() || "-"}
                    </InfoValue>
                  </AnalysisItem>
                  <AnalysisItem>
                    <InfoLabel>총 토큰</InfoLabel>
                    <InfoValue>
                      {record.analysis.tokens.total?.toLocaleString() || "-"}
                    </InfoValue>
                  </AnalysisItem>
                </>
              )}
              {record.analysis.promptId && (
                <AnalysisItem>
                  <InfoLabel>프롬프트 ID</InfoLabel>
                  <InfoValue>{record.analysis.promptId}</InfoValue>
                </AnalysisItem>
              )}
            </AnalysisGrid>
          </Section>
        )}
      </ContentWrapper>
    </PopupContainer>
  );
};

export default AdminConsultationRecordPopup;
